import { inject, Injectable } from "@angular/core";
import {
  ImageViewerLoaderPopupData,
  ImageViewerLoaderComponent
} from "src/app/components/ui/viewer/image-viewer/image-viewer-loader/image-viewer-loader.component";
import {
  PdfViewerLoaderPopupData,
  PdfViewerLoaderComponent
} from "src/app/components/ui/viewer/pdf-viewer/pdf-viewer-loader/pdf-viewer-loader.component";
import { PopupService } from "../popup/popup.service";
import { getMimeType } from "src/app/support/file";
import lightGallery from "lightgallery";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";
import lgVideo from "lightgallery/plugins/video";
import {
  blobFromArrayBufferAsync,
  blobToArrayBuffer,
  urlToBlob
} from "src/app/support/blob";

@Injectable()
export class ViewerService {
  private readonly popupService = inject(PopupService);
  private readonly videoExtensions = new Map([
    ["mp4", "video/mp4"],
    ["mov", "video/mov"],
    ["webm", "video/webm"]
  ]);

  async showPdf(name: string, url: string): Promise<void> {
    const data: PdfViewerLoaderPopupData = {
      name: name,
      src: url
    };
    await this.popupService.show(PdfViewerLoaderComponent, data);
  }

  async showImage(url: string): Promise<void> {
    const data: ImageViewerLoaderPopupData = {
      src: url
    };
    await this.popupService.show(ImageViewerLoaderComponent, data);
  }

  async showPdfLightBox(
    fileName: string,
    promiseBlob: Promise<Blob>
  ): Promise<void> {
    const blob = await promiseBlob;

    const cloned = new Blob([blob], { type: "application/pdf" });
    const objectUrl = URL.createObjectURL(cloned);

    const dynamicGallery = lightGallery(document.createElement("div"), {
      dynamic: true,
      plugins: [],
      dynamicEl: [
        {
          src: objectUrl,
          thumb: objectUrl,
          subHtml: "",
          iframe: true
        },
        {
          src: objectUrl,
          subHtml: "",
          thumb: objectUrl,
          iframe: true
        }
      ]
    });

    dynamicGallery.openGallery(0);
  }

  async downloadAndShowItems(
    items: Array<{
      src: string;
      thumbSrc?: string;
      fileName: string;
      iframe?: boolean;
    }>,
    startingIndex?: number
  ): Promise<void> {
    const downloadedItems = await Promise.all(
      items.map((item) => {
        return urlToBlob(item.src)
          .then((blob) => {
            return blobToArrayBuffer(blob);
          })
          .then((arrayBuffer: ArrayBuffer) => {
            return blobFromArrayBufferAsync(
              arrayBuffer,
              getMimeType(item.fileName) || undefined
            );
          })
          .then((typedBlob) => {
            const objectUrl = URL.createObjectURL(typedBlob);
            return {
              src: objectUrl,
              thumbSrc: item.thumbSrc || objectUrl,
              fileName: item.fileName,
              iframe: item.iframe
            };
          });
      })
    );

    await this.showItems(downloadedItems, startingIndex);
  }

  async showItems(
    items: Array<{
      src: string;
      thumbSrc?: string;
      fileName: string;
      iframe?: boolean;
    }>,
    startingIndex?: number
  ): Promise<void> {
    const dynamicGallery = lightGallery(document.createElement("div"), {
      dynamic: true,
      thumbnail: true,
      zoom: true,
      plugins: [lgThumbnail, lgZoom, lgVideo],
      dynamicEl: items.map((item) => {
        const fileNameSplit = item.fileName.split(".");
        const extension = fileNameSplit[fileNameSplit.length - 1];

        if (this.videoExtensions.has(extension)) {
          return <any>{
            download: item.fileName,
            thumb: "/assets/placeholders/placeholder_thumb_video.png",
            iframe: item.iframe,
            video: {
              source: [
                {
                  src: item.src,
                  type: this.videoExtensions.get(extension)
                }
              ],
              attributes: <any>{
                preload: false,
                controls: true
              }
            }
          };
        } else {
          return {
            src: item.src,
            download: item.fileName,
            thumb: item.fileName.toLowerCase().endsWith(".pdf")
              ? "/assets/placeholders/placeholder_thumb_pdf.png"
              : item.thumbSrc,
            iframe: item.iframe
          };
        }
      })
    });

    dynamicGallery.openGallery(startingIndex);
  }
}
